import { Inject, Injectable, ModuleWithProviders, Renderer2 } from '@angular/core';
import { Router } from '@angular/router';
import { DateTime, Duration, DurationObjectUnits, ToHumanDurationOptions } from 'luxon';
import { User, UserManagerSettings } from 'oidc-client-ts';
import { Observable, Subject } from 'rxjs';
import { PersonRoleEnum } from '../Models/OtherClasses';
import { parseUri } from '../RoutingAuth/parseUri';
import { AppModule } from '../app.module';

@Injectable({
	providedIn: 'root',
})
export class ConfigService {

	public user: User | null;

	static forRoot(): ModuleWithProviders<AppModule> {
		return {
			ngModule: AppModule,
			providers: [
			]
		};
	}
	constructor(
		@Inject("myconfig") private apiConfig: any,
		@Inject("BASE_URL") public baseUrl: string,

		private myRouter: Router) {
		this.AppConfig = apiConfig.AppConfig;
		this.DefaultEmptyGuid = apiConfig.DefaultEmptyGuid;
		this.TranslateWords = apiConfig.TranslateWords;
		this.allTimeZones = apiConfig.allTimeZones;
		this.gMapsApiKey = apiConfig.gMapsApiKey;
		this.viewingAsRole = apiConfig.ViewingAsRole;
		this.serverVersion = apiConfig.serverVersion;
		this.defaultLogo = apiConfig.defaultLogo;
		this.jsAppConfig = apiConfig.jsAppConfig;
		this.rootUrl = apiConfig.rootUrl;
	}


	innerDateReplaceToJSON(date: Date): string {
		//https://stackoverflow.com/questions/643782/how-to-check-whether-an-object-is-a-date
		if (date) {
			if (date instanceof Date) {

				//https://stackoverflow.com/questions/31096130/how-to-json-stringify-a-javascript-date-and-preserve-timezone
				var timezoneOffsetInHours = -(date.getTimezoneOffset() / 60); //UTC minus local time
				if (timezoneOffsetInHours == 0) {
					//If it's a UTC date, let's return it as such
					return date.toISOString();
				}
				var sign = timezoneOffsetInHours >= 0 ? '+' : '-';
				var leadingZero = (Math.abs(timezoneOffsetInHours) < 10) ? '0' : '';

				//It's a bit unfortunate that we need to construct a new Date instance 
				//(we don't want _this_ Date instance to be modified)
				var correctedDate = new Date(date.getFullYear(), date.getMonth(),
					date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(),
					date.getMilliseconds());
				correctedDate.setHours(date.getHours() + timezoneOffsetInHours);
				var iso = correctedDate.toISOString().replace('Z', '');

				return iso + sign + leadingZero + Math.abs(timezoneOffsetInHours).toString() + ':00';
			} else {
				return Object.prototype.toString.call(date);
			}
		}
		return '';
	}

	niceTimeFormat(minutes: number): string {
		var duration = Duration.fromObject({ minutes: minutes }).normalize()
			.shiftTo('days',
				'hours',
				'minutes');
		
		var pieces = [
			["d", duration.days],
			["h", duration.hours],
			["m", duration.minutes],

		].filter(a => parseInt(a[1].toString()) > 0)
			.map(a => a[1]+a[0].toString());
		
return pieces.join(' ');

		return duration.toHuman({ unitDisplay: 'short' } as ToHumanDurationOptions);
	}

	isSameDate(date1: Date, date2: Date): boolean {
		return this.getDateStr(date1) == this.getDateStr(date2);
	}

	getDateStr(date: Date): string {
		var convertDate = new Date(date);
		var dt = DateTime.fromJSDate(convertDate);
		if (date && date.toString().indexOf('Z') >= 0) {
			dt = dt.toUTC();
		}
		return dt.toISODate();

	}

	private menuChangedManuallySubject = new Subject<any>();
	menuChangedManually() {
		this.menuChangedManuallySubject.next({});
	}
	whenmenuChangedManually(): Observable<any> {
		return this.menuChangedManuallySubject.asObservable();
	}

	public apiBaseUrl() {
		return this.baseUrl;
	}
	public goHome() {
		this.myRouter.navigateByUrl(this.baseUrl);
	}

	blobToBase64(blob: Blob): Promise<string> {
		//https://stackoverflow.com/questions/18650168/convert-blob-to-base64
		const reader = new FileReader();
		reader.readAsDataURL(blob);
		return new Promise<string>(resolve => {
			reader.onloadend = () => resolve(reader.result.toString());
		});
	}

	public deepClone(src: any) {
		//return Object.assign({}, src);

		//https://saturncloud.io/blog/what-is-the-most-efficient-way-to-deep-clone-an-object-in-javascript/#method-3-recursive-function
		//https://stackoverflow.com/questions/122102/what-is-the-most-efficient-way-to-deep-clone-an-object-in-javascript/25476365
		if (typeof src !== "object" || src === null) {
			return src;
		}


		// Honor native/custom clone methods
		if (src.clone && toString.call(src.clone) == "[object Function]") {
			return null;
		}

		// DOM elements
		if (src.nodeType && toString.call(src.cloneNode) == "[object Function]") {
			return null;
		}

		// Date
		if (toString.call(src) == "[object Date]") {
			return new Date(src.getTime());
		}

		// RegExp
		if (toString.call(src) == "[object RegExp]") {
			return new RegExp(src);
		}

		// Function
		if (toString.call(src) == "[object Function]") {
			return null;
		}
		let clone : any = Array.isArray(src) ? [] : {};
		var invalidProperties = ['mapHolder','TruckMapPoints'];
		for (let key in src) {
			if (invalidProperties.indexOf(key) < 0) {
				clone[key] = this.deepClone(src[key]);
			} else {
				clone[key] = null;
			}
		}

		return clone;
	}

	public SortArray(myArray: any[], sortCriteria: TooBig4Car.Logic.SortCriteria) {
		if (!sortCriteria || !myArray || !myArray.length) {
			return myArray;
		}

		var cols: string[] = sortCriteria.sortColumn.split(',');
		var directions: string[] = sortCriteria.sortDirection.split(',');
		var dirLength = directions.length;
		return myArray.sort((a, b) => {

			for (var i = 0; i < cols.length; i++) {
				var colName = cols[i].trim();
				var firstCol: any = a[colName] ?? '';
				var secondCol: any = b[colName] ?? '';
				var direction = i + 1 <= dirLength ? directions[i] : directions[dirLength];

				var stringChanged: boolean = false;
				if (typeof (firstCol) == "string") {
					if (!firstCol) {
						firstCol = '';
					}
					firstCol = firstCol.toLowerCase();
					stringChanged = true;
				}
				if (typeof (secondCol) == "string") {
					if (!secondCol) {
						secondCol = '';
					}
					secondCol = secondCol.toLowerCase();
					stringChanged = true;
				}

				if (!stringChanged) {
					//If we need to sort addresses
					if (firstCol && secondCol && typeof (firstCol) == "object" && typeof (secondCol) == "object") {
						if (firstCol.Address1) {
							var stateCompare = this.sortValue(firstCol.State, secondCol.State, sortCriteria.sortDirection);
							if (stateCompare == 0) {
								var cityCompare = this.sortValue(firstCol.City, secondCol.City, sortCriteria.sortDirection);
								if (cityCompare == 0) {
									var addrCompare = this.sortValue(firstCol.Address1, secondCol.Address1, sortCriteria.sortDirection);
									if (addrCompare == 0) {
										var addrCompare2 = this.sortValue(firstCol.Address2, secondCol.Address2, sortCriteria.sortDirection);
										if (addrCompare != 0) {
											return addrCompare2;
										}
									} else {
										return addrCompare;
									}
								} else {
									return cityCompare;
								}
							} else {
								return stateCompare;
							}
						}
					}
				}
				if (firstCol != secondCol) {
					return this.sortValue(firstCol, secondCol, direction);
				}
			}
			return 0;
		});
	}

	private sortValue(a: any, b: any, sortType: string): number {
		if (a == b) {
			return 0;
		}

		if (sortType === 'desc') {
			return this.alphanum(b, a);
		} else {
			return this.alphanum(a, b);
		}

		//if (sortType === 'desc') {
		//	return a < b ? 1 : -1;
		//} else {
		//	return a < b ? -1 : 1;
		//}
	}

	public getSubDomain(uri: string): string {
		var pieces = parseUri(uri);
		var host: string = (pieces['host'] || '');
		var allChunks = host.split('.');
		if (allChunks && allChunks.length > 2) {
			return allChunks[0];
		}
		return '';
	}

	public roundNumber(num: number, decimalDigits: number) {
		if (!isNaN(num) && decimalDigits > 0) {
			var power = Math.pow(10, decimalDigits);
			return Math.round(num * power) / power;
		}
		return num;
	}
	public roundNumberUp(num: number, decimalDigits: number) {
		if (!isNaN(num) && decimalDigits > 0) {
			var power = Math.pow(10, decimalDigits);
			return Math.ceil(num * power) / power;
		}
		return num;
	}
	private isNumber(char: string): boolean {
		return !isNaN(parseFloat(char));
	}

	private isValidDate(date: any) {
		if (date) {
			var a = new Date(date.toString().toUpperCase());
			return Object.prototype.toString.call(a) === "[object Date]" && !isNaN(Number(a));
		}
		return false;
	}

	private alphanum(s1: any, s2: any): number {
		if (s1 == null) {
			return 0;
		}
		if (s2 == null) {
			return 0;
		}

		if (this.isValidDate(s1) && this.isValidDate(s2)) {
			return s1 > s2 ? 1 : s1 < s2 ? -1 : 0;
		}
		if (this.isNumber(s1) && this.isNumber(s2)) {
			return s1 - s2;
		}
		if ("string" != typeof (s1)) {
			s1 = s1.toString();
		}
		if ("string" != typeof (s2)) {
			s2 = s2.toString();
		}
		var len1: number = s1.length;
		var len2: number = s2.length;
		var marker1: number = 0;
		var marker2: number = 0;

		// Walk through two the strings with two markers.
		while (marker1 < len1 && marker2 < len2) {
			var ch1: string = s1[marker1];
			var ch2: string = s2[marker2];

			// Some buffers we can build up characters in for each chunk.
			var space1: string[] = [];
			var loc1: number = 0;
			var space2: string[] = [];
			var loc2: number = 0;

			// Walk through all following characters that are digits or
			// characters in BOTH strings starting at the appropriate marker.
			// Collect char arrays.
			do {
				space1[loc1++] = ch1;
				marker1++;

				if (marker1 < len1) {
					ch1 = s1[marker1];
				} else {
					break;
				}
			} while (this.isNumber(ch1) == this.isNumber(space1[0]));

			do {
				space2[loc2++] = ch2;
				marker2++;

				if (marker2 < len2) {
					ch2 = s2[marker2];
				} else {
					break;
				}
			} while (this.isNumber(ch2) == this.isNumber(space2[0]));

			// If we have collected numbers, compare them numerically.
			// Otherwise, if we have strings, compare them alphabetically.
			var str1: string = space1.join('');
			var str2: string = space2.join('');

			var result: number;

			if (this.isNumber(space1[0]) && this.isNumber(space2[0])) {
				var thisNumericChunk: number = parseInt(str1);
				var thatNumericChunk: number = parseInt(str2);
				result = thisNumericChunk == thatNumericChunk ? 0 : (thisNumericChunk > thatNumericChunk ? 1 : -1);
			} else {
				result = str1 == str2 ? 0 : (str1 > str2 ? 1 : -1)
			}

			if (result != 0) {
				return result;
			}
		}
		return len1 - len2;
	}

	//https://stackoverflow.com/questions/20156/is-there-an-easy-way-to-create-ordinals-in-c
	ordinalNumber(num: number) {
		if (num <= 0) return num.toString();

		switch (num % 100) {
			case 11:
			case 12:
			case 13:
				return num + "th";
		}

		switch (num % 10) {
			case 1:
				return num + "st";
			case 2:
				return num + "nd";
			case 3:
				return num + "rd";
			default:
				return num + "th";
		}
	}


	dayName(date: DateTime) {
		return date.weekdayLong;
	}

	ordinalWeekDay(date: DateTime) {
		var myVal: number = date.day;
		var dayNum = date.daysInMonth;
		var trackDate = DateTime.fromObject({ year: date.year, month: date.month, day: 1 });
		var weekNumber: number = 1;
		while (trackDate.day < dayNum) {
			if (trackDate.day == myVal) {
				weekNumber++;
			}
			trackDate = trackDate.plus({ days: 1 });
		}
		return this.ordinalNumber(weekNumber);
	}

	monthName(date: DateTime) {
		return date.monthLong;
	}

	public AddressToSingleLineWithoutWhiteSpace(address: TooBig4Car.Logic.Address): string {
		var addrStr = '';
		if (address) {
			addrStr += address.Address1 ?? '';
			if (address.Address2) {
				addrStr += address.Address2;
			}
			if (address.City) {
				addrStr += address.City;
			}
			if (address.State) {
				addrStr += address.State;
			}
			if (address.ZipCode) {
				addrStr += address.ZipCode;
			}
			addrStr = addrStr.replace(/\s+/g, '');
		}
		return addrStr;
	}

	public IsSameAddress(addr1: TooBig4Car.Logic.Address, addr2: TooBig4Car.Logic.Address): boolean {
		if (addr1 && addr2) {
			if (addr1.Latitude && addr2.Latitude && addr1.Latitude == addr2.Latitude) {
				if (addr1.Longitude && addr2.Longitude && addr1.Longitude == addr2.Longitude) {
					return true;
				}
			}
			var addr1Str = this.AddressToSingleLineWithoutWhiteSpace(addr1);
			var addr2Str = this.AddressToSingleLineWithoutWhiteSpace(addr2);
			if (addr1Str && addr2Str) {
				return addr1Str == addr2Str;
			}
		}
		return false;
	}

	public IsEmptyGuid(id: System.Guid) {
		if (id) {
			if (id != this.DefaultEmptyGuid) {
				return false;
			}
		}
		return true;
	}

	public getSavedSetting(settingName: string, pageName: string) {
		var storeName = (pageName || '') + (settingName || '');
		if (storeName) {
			var savedData = localStorage.getItem(storeName);
			if (savedData) {
				return JSON.parse(savedData);
			}
		}
		return null;

	}

	public setSavedSetting(settingName: string, pageName: string, obj: any) {
		var storeName = (pageName || '') + (settingName || '');
		localStorage.setItem(storeName, JSON.stringify(obj));
	}

	public name(): string {
		return this?.user?.profile['given_name']?.toString();
	}

	public customerName(): string {
		if (this.isCustomerUser()) {
			return this?.user?.profile['cust_name']?.toString();
		}
		return '';
	}

	public isLoggedIn(): boolean {
		return this.user?.access_token?.length > 0;
	}
	public isEmailConfirmed() {
		if (this.user && this.user.profile) {
			return this.user.profile.EmailConfirmed;
		}
		return false;
	}

	public userID() {
		if (this.user && this.user.profile) {
			return this.user.profile.sub || this.DefaultEmptyGuid;
		}
		return this.DefaultEmptyGuid;
	}

	public isSuperAdmin(): boolean {
		return this.IsUserInRole(PersonRoleEnum.SUPERADMIN);
	}
	public isEmployee(): boolean {
		return this.IsUserInRole(PersonRoleEnum.SYSTEMROLEEMPLOYEE)
			;			;
	}
	public isMasterCustomer(): boolean {
		return this.isCustomerUser()
			&& this.IsUserInRoleInternal(PersonRoleEnum.SYSTEMROLEMASTERCUSTOMERACCOUNT, false)
			;
	}
	public isSupportMode(): boolean {
		return this.IsUserInRoleInternal(PersonRoleEnum.SYSTEMROLECUSTOMERACCOUNT, false)
			&& (
				this.IsUserInRoleInternal(PersonRoleEnum.SUPPORTPERSON, false)
				||
				this.IsUserInRoleInternal(PersonRoleEnum.SYSTEMROLECUSTOMERMASTERPERSONACTIVE, false)
			)
			;
	}

	public isCustomerUser(): boolean {
		return this.IsUserInRoleInternal(PersonRoleEnum.SYSTEMROLECUSTOMERACCOUNT, false);
	}

	public isWarehouseCustomer(): boolean {
		return this.isCustomerUser()
			&& this.IsUserInRoleInternal(PersonRoleEnum.SYSTEMROLEWAREHOUSECUSTOMERACCOUNT, false);
	}
	public customerID(): any {
		if (this.isCustomerUser()) {
			if (this.user && this.user.profile) {
				return this.user.profile.cust_id || this.DefaultEmptyGuid;
			}
		}
		return this.DefaultEmptyGuid;
	}

	private _companyID: System.Guid = '';

	private defaultCompanyID() {
		if (this.user && this.user.profile) {
			return this.user.profile.CompanyID || this.DefaultEmptyGuid;
		}
		return this.DefaultEmptyGuid;
	}
	public get companyID(): System.Guid {
		if (!this.IsEmptyGuid(this._companyID)) {
			return this._companyID;
		}
		return this.defaultCompanyID();
	}
	public set companyID(val: System.Guid) {
		this._companyID = val;
	}
	public IsUserInRole(roleSystemCode: string) {
		return this.IsUserInRoleInternal(roleSystemCode, true);
	}
	public IsUserInRoleInternal(roleSystemCode: string, includeSystemAdminCheck: boolean) {
		if (!this.user) {
			return false;
		}
		var roles = (this.user.profile['role'] || '').toString().split(',');
		return roles.indexOf(roleSystemCode) >= 0
			||
			(includeSystemAdminCheck && roles.indexOf(PersonRoleEnum.SUPERADMIN) >= 0);
	}

	private static alreadyPoppedUp = false;
	public shouldShowVersionAlert(versionNum: string) {
		if (!ConfigService.alreadyPoppedUp && this.serverVersion != versionNum) {
			ConfigService.alreadyPoppedUp = true;
			return true;
		}
		return false;
	}

	public MakeTimeQS(): string {
		return '?q=' + (new Date()).getSeconds();
	}

	public AppConfig: ConfigDict;
	public DefaultEmptyGuid: System.Guid;
	public allTimeZones: TimeZoneDict;
	public TranslateWords: TranslateWordsDict;
	public viewingAsRole: string;
	public gMapsApiKey: string;
	public serverVersion: string;
	public defaultLogo: string;
	public rootUrl: string;
	public jsAppConfig: UserManagerSettings;

}


type TranslateWordsDict = { [key: string]: any };

type ConfigDict = { [key: string]: any };

type TimeZoneDict = { [key: string]: any };
